import { h } from '@omni-torn/ui';
import {
  ATTACK_ENERGY,
  barFlow,
  barsCsvRows,
  crimeNameIndex,
  drugRows,
  GYM_STATS,
  gymSums,
  happyJumps,
  inRange,
  keyMeets,
  nerveBreakdown,
  rangeText,
  readBarLine,
  readCrimesLite,
  skill100,
  sumPerDay,
  type BarLine,
  type DateRange,
  type Flow,
  type ItemRef,
} from '@omni-torn/shared';
import { forgetLogText, logKeptWhere, readingLogText } from '../own-history';
import { firstRead, LogReader } from '../log-kit/reader';
import { logStore, type LogStore } from '../log-kit/store';
import { csvButton, filterSelect, keptLogPanel, rangeFromAddress, rangePicker, setAddressQuery, zoomChart } from '../log-kit/ui';
import { readBars, readCooldowns } from '../read';
import { session, torn } from '../session';
import { box, duration, facts, gated, loading, num, problem, short, table, tabs, tct } from '../ui';
import { ref } from './torn';

/**
 * "Energy & nerve" (You → Energy & nerve, website roadmap S12 — torn.report's Energy, Gym, Nerve and Drugs pages), on the
 * log-report kit (S09):
 *  - Energy: what came in (Xanax, refills, cans, FHC…) and went out (the gym per stat, attacks, …), and the rest worked
 *    out (natural regeneration, mostly);
 *  - Gym: trains, energy and gains per stat, gains per 100 energy, happy jumps;
 *  - Nerve: in and out, by crime, outcome and your skill in the crime, busts, "Skill 100 only";
 *  - Drugs: uses per drug, overdoses, time between uses, all time, your drug cooldown now.
 * All-time totals come from your personal stats (one call); the rest from your log (Full key) kept in this browser by the
 * kit. Your own key only; nothing new goes to the Omni-Torn server (its 90-day log copy, when tracking is on, fills in).
 */

type Tab = 'energy' | 'gym' | 'nerve' | 'drugs';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'energy', label: 'Energy' },
  { id: 'gym', label: 'Gym' },
  { id: 'nerve', label: 'Nerve' },
  { id: 'drugs', label: 'Drugs' },
];
const LOG_NEED = { level: 4, user: ['log'] } as const;
const MINIMAL = { level: 2, user: ['bars'] } as const;
const STAT_NAME: Record<string, string> = { strength: 'Strength', defense: 'Defense', speed: 'Speed', dexterity: 'Dexterity', all: 'All stats' };

const tile = (label: string, value: string, data = '') => h('div', { class: 'tile', ...(data ? { 'data-tile': data } : {}) }, h('div', { class: 'tile-label' }, label), h('div', { class: 'tile-value' }, value));
const pct1 = (x: number | null) => (x === null ? '—' : num(x, 1));
const amountCell = (r: { amount: number; unknown: number; count: number }) => (r.unknown === r.count ? '—' : `${num(r.amount)}${r.unknown ? ` + ${r.unknown} not known` : ''}`);

/** Which lines a tab reads. */
function inTab(l: BarLine, tab: Tab): boolean {
  if (tab === 'gym') return l.kind === 'gym';
  if (tab === 'drugs') return l.group === 'drug';
  if (tab === 'energy') return l.kind !== 'crime' && l.kind !== 'bust' && l.kind !== 'overdose' && (l.energy !== null ? l.energy !== 0 : l.kind === 'item' || l.label === 'Energy refill' || l.kind === 'other');
  return l.kind === 'crime' || l.kind === 'bust' || (l.nerve !== null ? l.nerve !== 0 : l.label === 'Nerve refill');
}

/** An in/out table. */
function flowTable(rows: Flow['in'], what: string, empty: string): HTMLElement {
  return table(rows, [
    { label: 'From', get: (r) => r.label, sort: (r) => r.label },
    { label: 'Times', get: (r) => num(r.count), sort: (r) => r.count, cls: 'r' },
    { label: what, get: amountCell, sort: (r) => r.amount, cls: 'r' },
  ], { initial: 2, empty });
}

export async function renderEnergy(view: HTMLElement): Promise<void> {
  const me = session.get();
  if (!me) return;
  const pid = me.playerId;
  const q = new URLSearchParams(location.search);
  let tab: Tab = TABS.find((t) => t.id === q.get('tab'))?.id ?? 'energy';
  let range: DateRange = rangeFromAddress('30d');
  let only100 = q.get('cs100') === '1';

  // What the page knows besides the log: all-time totals (personal stats), bars' maximums, the drug cooldown, crime
  // skills (for nerve by skill), and Torn's items (names, types, effects — the server's list, none of your calls).
  let personal: Record<string, number> | null = null;
  let maxEnergy: number | null = null;
  let maxNerve: number | null = null;
  let drugCooldown: number | null = null;
  let skills = new Map<number, number>();
  let items = new Map<number, ItemRef>();

  const status = h('p', { class: 'muted small', 'data-energy-status': '' });
  const body = h('div', {});
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Energy & nerve'), h('p', { class: 'muted' }, `Where your energy and nerve came from and went: the gym per stat, happy jumps, crimes, busts and drugs — from your log, ${logKeptWhere()}.`)),
    tabs(TABS, tab, (id) => {
      tab = id as Tab;
      setAddressQuery(null, { tab: tab === 'energy' ? null : tab });
      draw();
    }),
    status,
    body,
  );

  // ------------------------------------------------------------ the log (the kit)

  let store: LogStore | null = null;
  let reader: LogReader | null = null;
  /** The first read this visit (the server's copy, then Torn's newest): every draw waits for it. */
  let first: Promise<void> | null = null;
  const logNote = h('span', { class: 'muted small', 'data-log-note': '' });

  async function ensureReader(): Promise<LogReader> {
    if (reader) return reader;
    store = await logStore(pid);
    // The whole log: energy, nerve, the gym and drugs are spread over many of Torn's log categories.
    reader = new LogReader(store, { id: 'all' });
    return reader;
  }

  const lineCtx = () => ({ items, crimeNames: crimeNameIndex(), maxEnergy, maxNerve });

  function drawLog(): HTMLElement {
    const out = h('div', {}, loading(readingLogText()));
    void (async () => {
      try {
        const r = await ensureReader();
        first ??= firstRead(r).then(
          (t) => void (logNote.textContent = t),
          (err: unknown) => void logNote.replaceChildren(problem(err)),
        );
        await first;
        if (out.isConnected) await fillLog(out);
      } catch (err) {
        out.replaceChildren(problem(err));
      }
    })();
    return out;
  }

  async function fillLog(out: HTMLElement): Promise<void> {
    const r = await ensureReader();
    const ctx = lineCtx();
    const all = (await store!.range({ from: null, to: null })).map((l) => readBarLine(l, ctx)).filter((l): l is BarLine => !!l);
    const mine = all.filter((l) => inTab(l, tab));
    const shown = mine.filter((l) => inRange(l.at, range));
    const reread = () => void fillLog(out);
    const setQ = () => setAddressQuery(range, { tab: tab === 'energy' ? null : tab, cs100: only100 && tab === 'nerve' ? '1' : null });
    const onRange = (nr: DateRange) => {
      range = nr;
      setQ();
      reread();
    };
    const kept = await keptLogPanel({
      reader: r,
      store: store!,
      range,
      matching: mine.length,
      what: { energy: 'about energy', gym: 'gym trains', nerve: 'about nerve', drugs: 'drugs' }[tab],
      note: logNote,
      forgetText: forgetLogText(),
      onForget: () => {
        reader = null;
        store = null;
      },
      redraw: reread,
    });
    const picker = rangePicker(range, onRange);
    const csv = csvButton(() => `${tab}-${rangeText(range).replace(/[^\w]+/g, '-').toLowerCase()}.csv`, () => barsCsvRows(shown));
    const linesBox = box(
      'Log lines',
      [
        table(shown.slice(0, 500), [
          { label: 'When', get: (l) => h('span', { title: new Date(l.at).toUTCString() }, tct(l.at)), sort: (l) => l.at },
          { label: 'What', get: (l) => (l.kind === 'overdose' ? `${l.label} — overdose` : l.label), sort: (l) => l.label },
          { label: 'Energy', get: (l) => (!l.energy ? '—' : `${l.energy > 0 ? '+' : ''}${num(l.energy)}`), sort: (l) => l.energy ?? 0, cls: 'r' },
          { label: 'Nerve', get: (l) => (!l.nerve ? '—' : `${l.nerve > 0 ? '+' : ''}${num(l.nerve)}`), sort: (l) => l.nerve ?? 0, cls: 'r' },
          ...(tab === 'gym' ? [{ label: 'Gained', get: (l: BarLine) => (l.gain ? `+${num(l.gain)}` : '—'), sort: (l: BarLine) => l.gain ?? 0, cls: 'r' }] : []),
        ], { csv: false, initial: 0, empty: 'Nothing in this range.' }),
        shown.length > 500 ? h('p', { class: 'muted' }, `Showing the newest 500 of ${num(shown.length)} — the CSV has them all.`) : null,
      ].filter(Boolean) as HTMLElement[],
      { id: 'log-lines', extra: csv },
    );
    const now = Date.now();
    const chart = (label: string, value: (l: BarLine) => number, lines: readonly BarLine[] = shown) =>
      box(h('h2', {}, `${label} per day`), zoomChart(sumPerDay(lines, value, range, now), { label, onZoom: onRange }), { id: 'log-chart' });

    let parts: Array<HTMLElement | null> = [];
    if (tab === 'energy') {
      const f = barFlow(shown, 'energy');
      const xanax = shown.filter((l) => l.kind === 'item' && l.item === 206).length;
      parts = [
        h('div', { class: 'tiles' }, tile('Energy in (from the log)', num(f.totalIn), 'energy-in'), tile('Energy out', num(f.totalOut), 'energy-out'), tile('Natural & the rest (worked out)', num(f.rest), 'energy-rest'), tile('Xanax', num(xanax), 'xanax')),
        chart('Energy spent', (l) => Math.max(0, -(l.energy ?? 0))),
        h('div', { class: 'cards two' }, box('Energy in', flowTable(f.in, 'Energy', 'Nothing came in from items or refills in this range.'), { id: 'energy-in' }), box('Energy out', flowTable(f.out, 'Energy', 'No energy spent in this range.'), { id: 'energy-out' })),
        h('p', { class: 'muted small' }, `“Natural & the rest” is what went out minus what the log shows coming in — mostly natural regeneration (Torn doesn’t log it). Items count by Torn’s own effect (Xanax +250, a can its amount); a refill fills to your maximum${maxEnergy ? ` (${num(maxEnergy)} now)` : ''} unless the line says. An attack costs ${ATTACK_ENERGY} energy unless the line says.`),
      ];
    } else if (tab === 'gym') {
      const sums = gymSums(shown);
      const allRow = sums.find((s) => s.stat === 'all')!;
      const { jumps, inJump } = happyJumps(all.filter((l) => inRange(l.at, range) && (l.kind === 'gym' || l.item === 197)));
      const usual = gymSums(shown.filter((l) => !inJump.has(l.id))).find((s) => s.stat === 'all')!;
      parts = [
        h('div', { class: 'tiles' }, tile('Trains', num(allRow.trains), 'gym-trains'), tile('Energy', num(allRow.energy), 'gym-energy'), tile('Gained', `+${num(allRow.gain)}`, 'gym-gain'), tile('Per 100 energy', pct1(allRow.per100), 'gym-per100'), tile('Happy jumps', num(jumps.length), 'gym-jumps')),
        chart('Stats gained', (l) => l.gain ?? 0),
        box('Per stat', table(sums, [
          { label: 'Stat', get: (s) => STAT_NAME[s.stat] ?? s.stat },
          { label: 'Trains', get: (s) => num(s.trains), cls: 'r' },
          { label: 'Energy', get: (s) => num(s.energy), cls: 'r' },
          { label: 'Gained', get: (s) => (s.gain ? `+${num(s.gain)}` : '—'), cls: 'r' },
          { label: 'Per 100 energy', get: (s) => pct1(s.per100), cls: 'r' },
          { label: 'Share of energy', get: (s) => (allRow.energy ? `${num((s.energy / allRow.energy) * 100, 0)}%` : '—'), cls: 'r' },
        ]), { id: 'gym-stats' }),
        box('Happy jumps', [
          jumps.length
            ? table(jumps, [
                { label: 'When', get: (j) => h('span', { title: new Date(j.at).toUTCString() }, tct(j.at)), sort: (j) => j.at },
                { label: 'Found by', get: (j) => (j.by === 'ecstasy' ? 'Ecstasy, then the gym' : 'Happiness in the log') },
                { label: 'Trains', get: (j) => num(j.trains), sort: (j) => j.trains, cls: 'r' },
                { label: 'Energy', get: (j) => num(j.energy), sort: (j) => j.energy, cls: 'r' },
                { label: 'Gained', get: (j) => GYM_STATS.filter((s) => j.gain[s] > 0).map((s) => `+${num(j.gain[s])} ${s}`).join(', ') || '—' },
                { label: 'Per 100 energy', get: (j) => pct1(j.per100), sort: (j) => j.per100 ?? -1, cls: 'r' },
              ], { initial: 0 })
            : h('p', { class: 'muted' }, 'No happy jumps in this range.'),
          h('p', { class: 'muted small', 'data-jump-usual': '' }, `A jump is the gym trains within two hours after an Ecstasy (or trains the log says were at very high happiness). Your usual trains in this range: ${pct1(usual.per100)} per 100 energy.`),
        ], { id: 'gym-jumps' }),
      ];
    } else if (tab === 'nerve') {
      const maxed = skill100(skills);
      const lines = only100 ? shown.filter((l) => l.kind !== 'crime' || (l.crime?.typeId != null && maxed.has(l.crime.typeId))) : shown;
      const f = barFlow(lines, 'nerve');
      const b = nerveBreakdown(lines, skills);
      const cs = h('label', { class: 'check' }, h('input', { type: 'checkbox', 'data-filter': 'cs100', ...(only100 ? { checked: true } : {}) }), ' Skill 100 only');
      cs.querySelector('input')!.addEventListener('change', (e) => {
        only100 = (e.target as HTMLInputElement).checked;
        setQ();
        reread();
      });
      const rowsTable = (rows: ReturnType<typeof nerveBreakdown>['byCrime'], first: string, withSkill = false) =>
        table(rows, [
          { label: first, get: (r) => r.label, sort: (r) => r.label },
          ...(withSkill ? [{ label: 'Skill now', get: (r: (typeof rows)[number]) => { const s = /^\d+$/.test(r.key) ? skills.get(Number(r.key)) : undefined; return s === undefined ? '—' : num(s, 2); }, cls: 'r' }] : []),
          { label: 'Attempts', get: (r) => num(r.attempts), sort: (r) => r.attempts, cls: 'r' },
          { label: 'Nerve', get: (r) => num(r.nerve), sort: (r) => r.nerve, cls: 'r' },
        ], { initial: withSkill ? 3 : 2, empty: 'No crimes in this range.' });
      parts = [
        h('div', { class: 'toolbar' }, cs),
        h('div', { class: 'tiles' }, tile('Nerve out', num(f.totalOut), 'nerve-out'), tile('Nerve in (from the log)', num(f.totalIn), 'nerve-in'), tile('Crimes', num(b.byCrime.reduce((a, r) => a + r.attempts, 0)), 'nerve-crimes'), tile('Busts', b.busts.attempts ? `${num(b.busts.success)} / ${num(b.busts.attempts)}` : '0', 'nerve-busts')),
        chart('Nerve spent', (l) => Math.max(0, -(l.nerve ?? 0)), lines),
        h('div', { class: 'cards two' }, box('Nerve in', flowTable(f.in, 'Nerve', 'Nothing came in from items or refills in this range.'), { id: 'nerve-in' }), box('Nerve out', flowTable(f.out, 'Nerve', 'No nerve spent in this range.'), { id: 'nerve-out' })),
        box('By crime', rowsTable(b.byCrime, 'Crime', true), { id: 'nerve-crime' }),
        h('div', { class: 'cards two' }, box('By outcome', rowsTable(b.byOutcome, 'Outcome'), { id: 'nerve-outcome' }), box('By your skill in the crime (now)', rowsTable(b.bySkill, 'Skill'), { id: 'nerve-skill' })),
        h('p', { class: 'muted small' }, `Skill is your skill in each crime now (personal stats), not when you did it — Torn’s log doesn’t say. Busts are counted; their nerve only where the log line gives it${b.busts.unknown ? ` (${num(b.busts.unknown)} without)` : ''}.`),
      ];
    } else {
      const rows = drugRows(shown, null);
      const ods = shown.filter((l) => l.kind === 'overdose').length;
      parts = [
        h('div', { class: 'tiles' }, tile('Drugs taken', num(rows.reduce((a, r) => a + r.uses, 0)), 'drug-uses'), tile('Overdoses', num(ods), 'drug-ods'), tile('Xanax', num(rows.find((r) => r.id === 206)?.uses ?? 0), 'drug-xanax')),
        chart('Drugs taken', (l) => (l.kind === 'item' ? 1 : 0)),
        box('Per drug', table(rows, [
          { label: 'Drug', get: (r) => r.name, sort: (r) => r.name },
          { label: 'Taken', get: (r) => num(r.uses), sort: (r) => r.uses, cls: 'r' },
          { label: 'Overdoses', get: (r) => num(r.overdoses), sort: (r) => r.overdoses, cls: 'r' },
          { label: 'Last', get: (r) => (r.last ? tct(r.last) : '—'), sort: (r) => r.last ?? 0 },
          { label: 'Time between', get: (r) => (r.gap === null ? '—' : duration(r.gap / 1000)), sort: (r) => r.gap ?? Infinity, cls: 'r' },
        ], { initial: 1, empty: 'No drugs in this range.' }), { id: 'drug-rows' }),
      ];
    }
    out.replaceChildren(...([kept, picker, ...parts, linesBox].filter(Boolean) as HTMLElement[]));
  }

  // ------------------------------------------------------------ all time (personal stats) and now

  function drawTotals(): HTMLElement | null {
    if (!personal && drugCooldown === null) return null;
    const p = personal ?? {};
    const v = (k: string) => (p[k] === undefined ? '—' : num(p[k]));
    if (tab === 'energy') return box('All time', facts([['Energy refills', v('refills')], ['Energy drinks', v('energydrinkused')], ['Xanax', v('xantaken')], ['Boosters', v('boostersused')]]), { id: 'all-time' });
    if (tab === 'nerve') return box('All time', facts([['Nerve refills', v('nerverefills')], ['Alcohol', v('alcoholused')], ['Criminal offenses', v('criminaloffenses')]]), { id: 'all-time' });
    if (tab === 'drugs') {
      const rows = drugRows([], personal).filter((r) => r.allTime !== null);
      return box(
        'All time',
        [
          facts([['Drug cooldown now', drugCooldown === null ? '—' : drugCooldown > 0 ? duration(drugCooldown) : 'none'], ['Overdoses', v('overdosed')], ['Drugs used', v('drugsused')]]),
          rows.length ? table(rows, [
            { label: 'Drug', get: (r) => r.name, sort: (r) => r.name },
            { label: 'Taken, all time', get: (r) => num(r.allTime), sort: (r) => r.allTime ?? 0, cls: 'r' },
          ], { initial: 1 }) : null,
        ].filter(Boolean) as HTMLElement[],
        { id: 'all-time' },
      );
    }
    return null;
  }

  function draw(): void {
    const what = { energy: 'your log shows where energy came from and went.', gym: 'your log shows every gym train and happy jump.', nerve: 'your log shows nerve by crime, outcome and skill.', drugs: 'your log shows every drug you took and your overdoses.' }[tab];
    body.replaceChildren(...([drawTotals(), gated('From your log', LOG_NEED, what, drawLog)].filter(Boolean) as HTMLElement[]));
  }

  draw();
  // Then what Torn says now: personal stats (+ bars and cooldowns with a Minimal key), crime skills, Torn's items.
  status.textContent = 'Reading your totals…';
  const minimal = keyMeets(session.get()?.access ?? null, MINIMAL);
  const reads = await Promise.allSettled([
    torn('/user/', { selections: minimal ? 'personalstats,bars,cooldowns' : 'personalstats' }, 10 * 60_000),
    ref('items'),
    keyMeets(session.get()?.access ?? null, { level: 2, user: ['crimes'] }) ? torn('/v2/user/personalstats', { cat: 'crimes' }, 10 * 60_000) : Promise.resolve(null),
  ]);
  const [user, itemList, lite] = reads;
  if (user.status === 'fulfilled') {
    const ps = (user.value as { personalstats?: Record<string, unknown> })?.personalstats;
    if (ps && typeof ps === 'object') personal = Object.fromEntries(Object.entries(ps).filter(([, x]) => typeof x === 'number')) as Record<string, number>;
    const bars = readBars(user.value, Date.now());
    maxEnergy = bars.energy?.maximum ?? null;
    maxNerve = bars.nerve?.maximum ?? null;
    drugCooldown = readCooldowns(user.value)?.drug ?? null;
    status.textContent = '';
  } else status.replaceChildren(problem(user.reason));
  if (itemList.status === 'fulfilled') items = new Map(itemList.value.data.map((i) => [i.id, { name: i.name, type: i.type, effect: i.effect }]));
  if (lite.status === 'fulfilled' && lite.value) skills = new Map(Object.entries(readCrimesLite(lite.value)?.skills ?? {}).map(([k, x]) => [Number(k), x]));
  draw();
}
